/**
 * Utility for exporting structured tabular data to standard RFC 4180 CSV files.
 * Compatible with Microsoft Excel, Google Sheets, Apple Numbers, and modern web browsers.
 *
 * Guarantees:
 * 1. UTF-8 Byte Order Mark (\uFEFF) prepended so Microsoft Excel parses UTF-8 encoding automatically.
 * 2. RFC 4180 compliant CRLF (\r\n) line terminators preventing Excel row-merging bugs.
 * 3. Robust cell escaping for quotes, commas, newlines, and special characters (%, #, etc.).
 * 4. Blob URL generation instead of data: URI, completely eliminating URL length limitations
 *    and browser URI-decoding truncation bugs.
 */

export function formatCsvCell(cell) {
    if (cell == null) return '';
    const str = String(cell);
    // If the cell contains double quotes, commas, or line breaks, enclose in double quotes and escape internal quotes
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

export function generateCsvContent(headers, rows) {
    const headerLine = headers.map(formatCsvCell).join(',');
    const rowLines = rows.map(row => row.map(formatCsvCell).join(','));
    // Prepend UTF-8 BOM (\uFEFF) for Microsoft Excel
    return '\uFEFF' + [headerLine, ...rowLines].join('\r\n');
}

export function exportToCsv(filename, headers, rows) {
    const csvContent = generateCsvContent(headers, rows);

    // In non-browser / test environments where Blob or document is not available
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return csvContent;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Check for createObjectURL support (jsdom or browsers)
    if (window.URL && typeof window.URL.createObjectURL === 'function') {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => {
            try {
                window.URL.revokeObjectURL(url);
            } catch (_) {
                // Ignore in testing environments
            }
        }, 1000);
    }
    return csvContent;
}
